import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { API } from "@/lib/api";

const serviceOptions = [
  { id: "pre", label: "Pre-Purchase Comprehensive" },
  { id: "four", label: "4-Point Insurance" },
  { id: "wind", label: "Wind Mitigation" },
  { id: "trio", label: "Full Trio Bundle (Save 15%)" },
  { id: "annual", label: "Annual Maintenance Inspection" },
  { id: "energy", label: "Energy Loss Audit" },
  { id: "newcon", label: "New Construction Inspection" },
  { id: "prelist", label: "Pre-Listing Inspection" },
  { id: "thermal", label: "Standalone Thermal Imaging" },
];

const timeSlots = ["8:00 AM", "10:30 AM", "1:00 PM", "3:30 PM"];
const steps = ["Service", "Property", "Schedule", "Details"];

const inputCls =
  "w-full rounded-xl border border-sandline bg-paper px-4 py-3 text-sm text-obsidian outline-none transition-colors focus:border-terra placeholder:text-slate-400";

export const BookingModal = ({ open, onClose, prefill }) => {
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    service: "", address: "", sqft: 2000, date: null, time_slot: "",
    name: "", email: "", phone: "", notes: "", quote: null,
  });

  useEffect(() => {
    if (open && prefill) {
      setForm((f) => ({
        ...f,
        service: prefill.service || f.service,
        sqft: prefill.sqft || f.sqft,
        quote: prefill.quote ?? f.quote,
      }));
      setStep(prefill.service ? 1 : 0);
    }
    if (open && !prefill) setStep(0);
  }, [open, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canNext = [
    !!form.service,
    form.address.trim().length > 3 && form.sqft >= 300,
    !!form.date && !!form.time_slot,
    form.name.trim() && /.+@.+\..+/.test(form.email) && form.phone.trim().length >= 7,
  ][step];

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        sqft: Number(form.sqft),
        service: form.service,
        date: format(form.date, "EEEE, MMMM d, yyyy"),
        time_slot: form.time_slot,
        notes: form.notes,
        quote_estimate: form.quote,
      });
      toast.success("Inspection request received", {
        description: "We'll confirm your slot within the hour during business hours.",
      });
      onClose();
      setForm({ service: "", address: "", sqft: 2000, date: null, time_slot: "", name: "", email: "", phone: "", notes: "", quote: null });
      setStep(0);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not submit request. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl bg-sand border-sandline p-0 overflow-hidden" data-testid="booking-modal">
        <DialogHeader className="px-7 pt-7 pb-4">
          <div className="font-mono text-[10px] tracking-[0.25em] text-terra uppercase font-semibold">
            Online Scheduling — Step {step + 1} of 4
          </div>
          <DialogTitle className="font-display font-bold text-2xl text-obsidian tracking-tight">
            {steps[step] === "Details" ? "Your contact details" : `Select ${steps[step].toLowerCase()}`}
          </DialogTitle>
          <div className="flex gap-1.5 pt-3">
            {steps.map((s, i) => (
              <div key={s} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-terra" : "bg-sandline"}`} />
            ))}
          </div>
        </DialogHeader>

        <div className="px-7 pb-7 min-h-[300px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {step === 0 && (
                <div className="grid gap-3" data-testid="booking-form-step-1">
                  {serviceOptions.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`booking-service-${s.id}`}
                      onClick={() => set("service", s.label)}
                      className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left transition-all duration-300 ${
                        form.service === s.label ? "border-terra bg-terra/5" : "border-sandline hover:border-obsidian/30"
                      }`}
                    >
                      <span className="font-semibold text-sm text-obsidian">{s.label}</span>
                      {form.service === s.label && <CheckCircle2 size={18} className="text-terra" />}
                    </button>
                  ))}
                </div>
              )}

              {step === 1 && (
                <div className="space-y-4" data-testid="booking-form-step-2">
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">Property Address</label>
                    <input
                      data-testid="booking-address-input"
                      className={inputCls}
                      placeholder="123 Palm Harbor Dr, Tampa, FL"
                      value={form.address}
                      onChange={(e) => set("address", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">Approx. Square Footage</label>
                    <input
                      data-testid="booking-sqft-input"
                      type="number"
                      min={300}
                      className={inputCls}
                      value={form.sqft}
                      onChange={(e) => set("sqft", e.target.value)}
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="grid sm:grid-cols-2 gap-5" data-testid="booking-form-step-3">
                  <div className="rounded-xl border border-sandline bg-paper p-2 flex justify-center">
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(d) => set("date", d)}
                      disabled={{ before: new Date() }}
                      data-testid="booking-calendar"
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">Time Slot</label>
                    <div className="grid gap-2.5">
                      {timeSlots.map((t) => (
                        <button
                          key={t}
                          data-testid={`booking-slot-${t.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                          onClick={() => set("time_slot", t)}
                          className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all duration-300 ${
                            form.time_slot === t ? "border-terra bg-terra text-sand" : "border-sandline text-slate-600 hover:border-obsidian/30"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4" data-testid="booking-form-step-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="booking-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                    <input data-testid="booking-phone-input" className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  </div>
                  <input data-testid="booking-email-input" className={inputCls} placeholder="Email address" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                  <textarea
                    data-testid="booking-notes-input"
                    className={`${inputCls} min-h-[80px] resize-none`}
                    placeholder="Anything we should know? (gate code, pets, concerns…)"
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                  />
                  <div className="rounded-xl bg-obsidian text-sand px-5 py-4 text-sm flex flex-wrap gap-x-6 gap-y-1">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-sand/50 w-full">Summary</span>
                    <span>{form.service}</span>
                    <span>{form.date ? format(form.date, "MMM d, yyyy") : ""} · {form.time_slot}</span>
                    {form.quote && <span className="text-sun font-semibold">Est. ${form.quote}</span>}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-7 flex items-center justify-between">
            <button
              data-testid="booking-back-button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="flex items-center gap-2 text-sm font-semibold text-slate-500 disabled:opacity-30 transition-colors hover:text-obsidian"
            >
              <ArrowLeft size={15} /> Back
            </button>
            {step < 3 ? (
              <button
                data-testid="booking-next-button"
                onClick={() => canNext && setStep((s) => s + 1)}
                disabled={!canNext}
                className="flex items-center gap-2 bg-obsidian text-sand font-semibold text-sm px-6 py-3 rounded-full transition-all duration-300 hover:bg-terra disabled:opacity-30"
              >
                Continue <ArrowRight size={15} />
              </button>
            ) : (
              <button
                data-testid="booking-form-submit-button"
                onClick={submit}
                disabled={!canNext || sending}
                className="flex items-center gap-2 bg-terra text-sand font-semibold text-sm px-6 py-3 rounded-full transition-all duration-300 hover:scale-[1.03] disabled:opacity-40"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                {sending ? "Submitting…" : "Confirm Inspection Request"}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
